// RITA-1602 — Volltextsuche über Kapitelnamen und Handbuchtext. Groß-/Kleinschreibung ist egal, ß und ss gelten gleich,
// mehrere Wörter müssen alle im selben Kapitel vorkommen. Kapitelnamen werden sofort durchsucht, der Text, sobald er
// geladen ist.
import { blockText, HandbuchInhalt, Kapitelinhalt } from './handbuchInhalt';
import { Handbuch, Handbuchkapitel, kapitelBezeichnung } from './handbuecher';

function zeichenNormalisieren(zeichen: string): string {
  if (zeichen === 'ß' || zeichen === 'ẞ') return 'ss';
  if (zeichen === ' ') return ' ';
  return zeichen.toLocaleLowerCase('de-DE');
}

export function suchNormalform(text: string): string {
  return Array.from(text).map(zeichenNormalisieren).join('');
}

export function suchwoerter(begriff: string): string[] {
  return suchNormalform(begriff.trim()).split(/\s+/).filter(Boolean);
}

export interface Fundstelle {
  von: number;
  bis: number;
}

/** Alle Fundstellen der Wörter im Originaltext, sortiert und ohne Überschneidung (Stellen beziehen sich aufs Original). */
export function fundstellen(text: string, woerter: readonly string[]): Fundstelle[] {
  if (woerter.length === 0 || !text) return [];
  let normal = '';
  const herkunft: number[] = [];
  let stelle = 0;
  Array.from(text).forEach((zeichen) => {
    const umgewandelt = zeichenNormalisieren(zeichen);
    for (let i = 0; i < umgewandelt.length; i += 1) herkunft.push(stelle);
    normal += umgewandelt;
    stelle += zeichen.length;
  });
  herkunft.push(stelle);
  const roh: Fundstelle[] = [];
  woerter.forEach((wort) => {
    let treffer = normal.indexOf(wort);
    while (treffer >= 0) {
      roh.push({ von: herkunft[treffer], bis: herkunft[treffer + wort.length] });
      treffer = normal.indexOf(wort, treffer + wort.length);
    }
  });
  roh.sort((a, b) => a.von - b.von);
  return roh.reduce<Fundstelle[]>((ergebnis, eintrag) => {
    const letzte = ergebnis[ergebnis.length - 1];
    if (letzte && eintrag.von <= letzte.bis) letzte.bis = Math.max(letzte.bis, eintrag.bis);
    else ergebnis.push({ ...eintrag });
    return ergebnis;
  }, []);
}

export interface TextAbschnitt {
  text: string;
  markiert: boolean;
}

/** Zerlegt Text in markierte und unmarkierte Stücke, für Ausschnitt und Anzeige. */
export function textMarkieren(text: string, woerter: readonly string[]): TextAbschnitt[] {
  const stellen = fundstellen(text, woerter);
  if (stellen.length === 0) return [{ text, markiert: false }];
  const ergebnis: TextAbschnitt[] = [];
  let position = 0;
  stellen.forEach(({ von, bis }) => {
    if (von > position) ergebnis.push({ text: text.slice(position, von), markiert: false });
    ergebnis.push({ text: text.slice(von, bis), markiert: true });
    position = bis;
  });
  if (position < text.length) ergebnis.push({ text: text.slice(position), markiert: false });
  return ergebnis;
}

const AUSSCHNITT_LUFT = 48;

/** Satzstück rund um den ersten Fund, an Wortgrenzen abgeschnitten. */
export function fundAusschnitt(text: string, woerter: readonly string[]): string {
  const [erste] = fundstellen(text, woerter);
  if (!erste) return '';
  let von = Math.max(0, erste.von - AUSSCHNITT_LUFT);
  let bis = Math.min(text.length, erste.bis + AUSSCHNITT_LUFT);
  if (von > 0) {
    const leer = text.indexOf(' ', von);
    if (leer >= 0 && leer < erste.von) von = leer + 1;
  }
  if (bis < text.length) {
    const leer = text.lastIndexOf(' ', bis);
    if (leer > erste.bis) bis = leer;
  }
  return `${von > 0 ? '… ' : ''}${text.slice(von, bis).trim()}${bis < text.length ? ' …' : ''}`;
}

interface Blockspur {
  stelle: number;
  text: string;
  normal: string;
}

const spurSpeicher = new WeakMap<Kapitelinhalt, Blockspur[]>();

function blockspur(kapitel: Kapitelinhalt): Blockspur[] {
  const vorhanden = spurSpeicher.get(kapitel);
  if (vorhanden) return vorhanden;
  const spur = kapitel.bloecke.map((block, stelle) => {
    const text = blockText(block);
    return { stelle, text, normal: suchNormalform(text) };
  });
  spurSpeicher.set(kapitel, spur);
  return spur;
}

function spurVon(inhalt: HandbuchInhalt | undefined, kapitelId: string): Blockspur[] {
  const kapitel = inhalt?.kapitel.find((eintrag) => eintrag.id === kapitelId);
  return kapitel ? blockspur(kapitel) : [];
}

function anzahlIn(normal: string, wort: string): number {
  let anzahl = 0;
  let treffer = normal.indexOf(wort);
  while (treffer >= 0) {
    anzahl += 1;
    treffer = normal.indexOf(wort, treffer + wort.length);
  }
  return anzahl;
}

export interface Suchtreffer {
  handbuch: Handbuch;
  kapitel: Handbuchkapitel;
  /** Satzstück aus dem Text; fehlt, wenn nur der Kapitelname passt. */
  ausschnitt?: string;
  /** Stelle des Bausteins im Kapitel, an den der Treffer springt. */
  block?: number;
  imNamen: boolean;
  anzahl: number;
}

export type Handbuchtexte = Readonly<Record<string, HandbuchInhalt | undefined>>;

/**
 * Suche über die übergebenen Handbücher. Reihenfolge: Treffer im Kapitelnamen zuerst, dann Kapitel mit den meisten
 * Fundstellen, bei Gleichstand die Reihenfolge im Handbuch. Leere Eingabe: keine Treffer.
 */
export function handbuchSuchen(
  begriff: string,
  handbuecher: readonly Handbuch[],
  texte: Handbuchtexte = {},
): Suchtreffer[] {
  const woerter = suchwoerter(begriff);
  if (woerter.length === 0) return [];
  const treffer: (Suchtreffer & { reihe: number })[] = [];
  handbuecher.forEach((handbuch) => {
    const inhalt = texte[handbuch.id];
    handbuch.kapitel.forEach((kapitel) => {
      const name = suchNormalform(kapitelBezeichnung(kapitel));
      const spur = spurVon(inhalt, kapitel.id);
      const passt = woerter.every(
        (wort) => name.includes(wort) || spur.some((eintrag) => eintrag.normal.includes(wort)),
      );
      if (!passt) return;
      const imNamen = woerter.every((wort) => name.includes(wort));
      const anzahl = woerter.reduce(
        (summe, wort) => summe + spur.reduce((teil, eintrag) => teil + anzahlIn(eintrag.normal, wort), 0),
        0,
      );
      const besterBlock =
        spur.find((eintrag) => woerter.every((wort) => eintrag.normal.includes(wort))) ??
        spur.find((eintrag) => woerter.some((wort) => eintrag.normal.includes(wort)));
      treffer.push({
        handbuch,
        kapitel,
        imNamen,
        anzahl,
        reihe: treffer.length,
        ...(besterBlock ? { block: besterBlock.stelle, ausschnitt: fundAusschnitt(besterBlock.text, woerter) } : {}),
      });
    });
  });
  return treffer
    .sort((a, b) => Number(b.imNamen) - Number(a.imNamen) || b.anzahl - a.anzahl || a.reihe - b.reihe)
    .map(({ reihe: _reihe, ...eintrag }) => eintrag);
}
